import React, { useState } from 'react';
import { X, Eye, EyeOff, Copy, Check } from 'lucide-react';
import { toast } from 'react-toastify';

const CreateUserModal = ({ isOpen, onClose, onSuccess, currentUserRole }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    role: currentUserRole === 'super_admin' ? 'admin' : 'staff'
  });
  const [generatePassword, setGeneratePassword] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [copied, setCopied] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(temporaryPassword);
    setCopied(true);
    toast.success('Password copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare data
      const userData = {
        full_name: formData.full_name,
        email: formData.email
      };

      // Only include password if not generating
      if (!generatePassword && formData.password) {
        userData.password = formData.password;
      }

      // Call appropriate API based on role
      const response = formData.role === 'admin'
        ? await onSuccess(userData, 'admin')
        : await onSuccess(userData, 'staff');

      if (response.data?.temporary_password) {
        setTemporaryPassword(response.data.temporary_password);
        setCreatedUser(response.data.user);
      } else {
        toast.success(`${formData.role === 'admin' ? 'Admin' : 'Staff'} user created successfully`);
        handleClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      full_name: '',
      email: '',
      password: '',
      role: currentUserRole === 'super_admin' ? 'admin' : 'staff'
    });
    setGeneratePassword(true);
    setCreatedUser(null);
    setTemporaryPassword('');
    setCopied(false);
    onClose();
  };

  if (!isOpen) return null;

  // Show success screen with temporary password
  if (createdUser && temporaryPassword) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-primary-black">
              User Created Successfully
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-green-800 mb-2">
              <strong>{createdUser.full_name}</strong> has been created with the email <strong>{createdUser.email}</strong>
            </p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Temporary Password
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={temporaryPassword}
                readOnly
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 font-mono text-sm"
              />
              <button
                onClick={handleCopyPassword}
                className="px-4 py-2 bg-accent-gold text-primary-black rounded-lg hover:bg-accent-goldDark transition-colors flex items-center gap-2"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-yellow-800">
              <strong>Important:</strong> Please provide this temporary password to the user securely.
              They will be required to change it on first login.
            </p>
          </div>

          <button
            onClick={handleClose}
            className="w-full py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // Show create user form
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-primary-black">
            Add New User
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Role Selection (if super admin) */}
          {currentUserRole === 'super_admin' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role *
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                required
              >
                <option value="admin">Admin</option>
                <option value="staff">Staff</option>
              </select>
            </div>
          )}

          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
              placeholder="Enter full name"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
              placeholder="Enter email address"
              required
            />
          </div>

          {/* Password Generation Option */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="generatePassword"
              checked={generatePassword}
              onChange={(e) => setGeneratePassword(e.target.checked)}
              className="w-4 h-4 text-accent-gold focus:ring-accent-gold border-gray-300 rounded"
            />
            <label htmlFor="generatePassword" className="text-sm text-gray-700">
              Auto-generate secure password
            </label>
          </div>

          {/* Manual Password Input (if not generating) */}
          {!generatePassword && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent pr-10"
                  placeholder="Enter password"
                  required={!generatePassword}
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Minimum 8 characters, include uppercase, lowercase, number, and special character
              </p>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              {generatePassword
                ? 'A secure temporary password will be generated and displayed after creation.'
                : 'The user will receive this password and be required to change it on first login.'}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUserModal;
